import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { differenceBy, flatten } from 'lodash-es';
import { Button, Hint, Input } from '@bucketplace/commerce-admin-ui';
import { FONT_STYLE, useSnackbar } from '@bucketplace/design-system';

import { FormContent } from '../../../components';
import { useCouponContext } from '../../../contexts';
import { useFieldForm } from '../../../hooks';
import { covertStringToArray, validateStringNumber } from '../../../utils';
import { CouponMold, ProductCouponMold } from '../type';
import { getGroupedCouponMolds, getInvalidCouponMolds, getInvalidProductCouponMolds, INVALID_MESSAGE, InvalidCouponMold } from './utils';

const MAXIMUM_COUPON_MOLDS = 100;

export interface IdFormGroupProps {
  /** 장바구니 쿠폰틀 id들 */
  cartCouponMoldId: string;
  /** 상품쿠폰틀 id들 */
  productCouponMoldId?: string;
  /** id를 기반으로 쿠폰틀 조회 후 호출됩니다.
   * @param couponMolds 장바구니 쿠폰틀 id들
   * @param couponMolds 상품 쿠폰틀 id들
   */
  onChange: (couponMoldIds: string[], productCouponIds: string[]) => void;
}

export function IdFormGroup({
  cartCouponMoldId: initialCartCouponMoldId,
  productCouponMoldId: initialProductCouponMoldId,
  onChange,
}: IdFormGroupProps): ReactElement {
  const { open } = useSnackbar();

  const [invalidCouponMolds, setInvalidCouponMolds] = useState<InvalidCouponMold[][]>();
  const [validCouponMolds, setValidCouponMolds] = useState<CouponMold[]>([]);
  const [validProductCouponMolds, setValidProductCouponMolds] = useState<ProductCouponMold[]>([]);

  const { exhibitionInfo, getCoupons, getProductCoupons } = useCouponContext();

  const { fields, errors, register, onChange: handleChange, trigger, setError } = useFieldForm();

  const { cartCouponMoldId, productCouponMoldId } = fields;
  const hasInvalidId = !!(errors?.cartCouponMoldId || errors?.productCouponMoldId);

  const Description = useMemo(() => invalidCouponMolds?.map((invalidMolds, index) => (
    <InfoList key={`${index + 1}`}>
      { invalidMolds[0]?.errorType && <p>{ INVALID_MESSAGE[invalidMolds[0].errorType] }</p> }
      { invalidMolds.map(({ id, message }) => (
        <div key={id}>
          { message }
        </div>
      )) }
    </InfoList>
  )),
  [invalidCouponMolds]);

  const getCartCouponMolds = async (): Promise<{
    validCouponMolds: CouponMold[],
    invalidCouponMolds: InvalidCouponMold[],
  }> => {
    const formattedIds = covertStringToArray(cartCouponMoldId);
    const couponMolds = await getCoupons?.(formattedIds) ?? [];
    const failedIds = formattedIds.filter((id) => !couponMolds.some((mold) => mold.id.toString() === id));

    const notFoundCouponMolds: InvalidCouponMold[] = failedIds.map((id) => ({
      id,
      errorType: 'NOT_FOUND',
      isError: true,
      message: `장바구니 쿠폰 : ${id} - 쿠폰을 찾을 수 없습니다.`,
    }));

    const invalidCouponMolds = notFoundCouponMolds.length > 0
      ? [
        ...notFoundCouponMolds,
        ...getInvalidCouponMolds(couponMolds, exhibitionInfo),
      ]
      : getInvalidCouponMolds(couponMolds, exhibitionInfo);

    const filetedInvalidCouponMolds = invalidCouponMolds.filter(({ isError }) => isError);

    const validCouponMolds = differenceBy(couponMolds, flatten(filetedInvalidCouponMolds), 'id');

    return {
      validCouponMolds,
      invalidCouponMolds,
    };
  };

  const getProductCouponMolds = async (): Promise<{
    validCouponMolds: ProductCouponMold[],
    invalidCouponMolds: InvalidCouponMold[],
  }> => {
    const formattedIds = covertStringToArray(productCouponMoldId);
    const couponMolds = await getProductCoupons?.(formattedIds) ?? [];
    const failedIds = formattedIds.filter((id) => !couponMolds.some((mold) => mold.couponMoldId.toString() === id));

    const notFoundCouponMolds: InvalidCouponMold[] = failedIds.map((id) => ({
      id,
      errorType: 'NOT_FOUND',
      isError: true,
      message: `상품 쿠폰 : ${id} - 쿠폰을 찾을 수 없습니다.`,
    }));

    const invalidCouponMolds = notFoundCouponMolds.length > 0
      ? [
        ...notFoundCouponMolds,
        ...getInvalidProductCouponMolds(couponMolds, exhibitionInfo),
      ]
      : getInvalidProductCouponMolds(couponMolds, exhibitionInfo);

    const filetedInvalidCouponMolds = invalidCouponMolds.filter(({ isError }) => isError);

    const validCouponMolds = differenceBy(couponMolds, filetedInvalidCouponMolds, (item): string | undefined => {
      if ('couponMoldId' in item) {
        return item.couponMoldId.toString();
      } else if ('id' in item) {
        return item.id.toString();
      }
      return undefined;
    });

    return {
      validCouponMolds,
      invalidCouponMolds,
    };
  };

  const checkCouponValidation = async (): Promise<void> => {
    const formattedCartIds = covertStringToArray(cartCouponMoldId);
    const formattedProductIds = covertStringToArray(productCouponMoldId);

    // 장바구니 쿠폰 이관으로 장바구니쿠폰틀ID가 100만 이상인 경우 입력 불가
    const isCartCouponMoldIdOverMillion = formattedCartIds.some((id) => Number(id) < 1_000_000);
    if (isCartCouponMoldIdOverMillion) {
      alert('장바구니 쿠폰틀 ID는 1,000,000 이상이어야 합니다.\n어드민을 통해 ID를 확인해주세요.');
      return;
    }

    if ((formattedCartIds.length + formattedProductIds.length) > MAXIMUM_COUPON_MOLDS) {
      alert(`쿠폰틀은 ${MAXIMUM_COUPON_MOLDS}개까지만 등록할 수 있습니다.`);
      return;
    }

    try {
      const {
        validCouponMolds: validCartCouponMolds,
        invalidCouponMolds: invalidCartCouponMolds,
      } = await getCartCouponMolds();
      const {
        validCouponMolds: validProductCouponMolds,
        invalidCouponMolds: invalidProductCouponMolds,
      } = await getProductCouponMolds();

      setValidCouponMolds(validCartCouponMolds);
      setValidProductCouponMolds(validProductCouponMolds);

      const groupedInvalidMolds = getGroupedCouponMolds(invalidCartCouponMolds, invalidProductCouponMolds);
      setInvalidCouponMolds(groupedInvalidMolds);

      const validCartCouponIds = validCartCouponMolds.map(({ id }) => id);
      const validProductCouponIds = validProductCouponMolds.map(({ couponMoldId }) => couponMoldId.toString());

      onChange(validCartCouponIds, validProductCouponIds);
    } catch (error) {
      const message = (error as Error).message ?? '쿠폰틀을 추가할 수 없습니다.';
      setError('cartCouponMoldId', message);
      setError('productCouponMoldId', message);
    }
  };

  const getCartCouponData = async (id: string): Promise<void> => {
    const couponData = await getCoupons?.(covertStringToArray(id));
    setValidCouponMolds(couponData ?? []);
  };

  const getProductCouponData = async (id: string): Promise<void> => {
    const couponData = await getProductCoupons?.(covertStringToArray(id));
    setValidProductCouponMolds(couponData ?? []);
  };

  useEffect(() => {
    register('cartCouponMoldId', {
      defaultValue: initialCartCouponMoldId,
      validate: (value) => {
        if (!validateStringNumber(value)) {
          return '숫자와 쉼표만 입력할 수 있습니다.';
        }
      },
    });
    register('productCouponMoldId', {
      defaultValue: initialProductCouponMoldId,
      validate: (value) => {
        if (!validateStringNumber(value)) {
          return '숫자와 쉼표만 입력할 수 있습니다.';
        }
      },
    });
  }, []);

  useEffect(() => {
    getCartCouponData(initialCartCouponMoldId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCartCouponMoldId]);

  useEffect(() => {
    getProductCouponData(initialProductCouponMoldId ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProductCouponMoldId]);

  useEffect(() => {
    const hasInvalid = invalidCouponMolds && invalidCouponMolds?.length > 0;
    if (!hasInvalid) {
      return;
    }

    open({
      content: Description,
      multiline: true,
      duration: 5000,
      onClose: () => setInvalidCouponMolds(undefined),
      buttons: [{
        children: '확인',
      }],
    });
  }, [Description, invalidCouponMolds]);

  return (
    <>
      <FormContent
        header="장바구니 쿠폰틀 ID"
        direction="VERTICAL"
      >
        <Input
          data-testid="cartCouponMoldId"
          name="cartCouponMoldId"
          size={32}
          placeholder="쿠폰팩으로 사용시 쉼표나 공백으로 구분해주세요."
          value={cartCouponMoldId}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
          onBlur={() => trigger('cartCouponMoldId')}
          error={!!errors?.cartCouponMoldId}
        />
        { errors?.cartCouponMoldId && <Hint error>{ errors?.cartCouponMoldId }</Hint> }
      </FormContent>
      <MoldInfoContainer>
        { validCouponMolds.map(({ id, title }) => (
          <InfoItem key={`${id}_${title}`}>
            <ValidHighlight>{ `# ${id}` }</ValidHighlight>
            { title }
          </InfoItem>
        )) }
      </MoldInfoContainer>
      <FormContent
        header="상품쿠폰틀 ID"
        direction="VERTICAL"
      >
        <Input
          data-testid="productCouponMoldId"
          name="productCouponMoldId"
          size={32}
          placeholder="쿠폰팩으로 사용시 쉼표나 공백으로 구분해주세요."
          value={productCouponMoldId}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
          onBlur={() => trigger('productCouponMoldId')}
          error={!!errors?.productCouponMoldId}
        />
        { errors?.productCouponMoldId && <Hint error>{ errors?.productCouponMoldId }</Hint> }
      </FormContent>
      <MoldInfoContainer>
        { validProductCouponMolds.map(({ couponMoldId, couponName }) => (
          <InfoItem key={`${couponMoldId}_${couponName}`}>
            <ValidHighlight>{ `# ${couponMoldId}` }</ValidHighlight>
            { couponName }
          </InfoItem>
        )) }
      </MoldInfoContainer>
      <SubmitButtonWrapper>
        <Button
          data-testid="submit-coupon-mold"
          disabled={hasInvalidId}
          size={32}
          full
          onClick={checkCouponValidation}
        >
          등록
        </Button>
      </SubmitButtonWrapper>
    </>
  );
}

const MoldInfoContainer = styled.div`
  padding: 8px 15px;
`;

const ValidHighlight = styled.em`
  ${FONT_STYLE.body2};
  font-weight: 700;
  color: ${({ theme }) => theme.content.primary1.basic};
  margin-right: 15px;
`;

const InfoList = styled.li`
  padding: 8px 0;
  list-style: none;
`;

const InfoItem = styled.li`
  padding: 4px 0;
  list-style: none;
`;

const SubmitButtonWrapper = styled.div`
  padding: 8px 15px;
`;
